import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@optools/components/ui/button';
import { DialogFooter } from '@optools/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@optools/components/ui/form';
import { Input } from '@optools/components/ui/input';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

const schema = z.object({
  countryCode: z.string().min(1, { error: 'Country Code is required' }),
  ipAddress: z.union([
    z.ipv4({ error: 'Please enter a valid IP address' }),
    z.ipv6({ error: 'Please enter a valid IP address' }),
  ]),
  oktaId: z.string().min(1, { error: 'Okta ID is required' }),
  zipCode: z.string().min(1, { error: 'Zip Code is required' }),
});

export type OverrideFormValues = z.infer<typeof schema>;

export interface OverrideFormProps {
  loading: boolean;
  onCancel: () => void;
  onSubmit: (values: OverrideFormValues) => void;
  override?: OverrideFormValues;
}

export function OverrideForm({
  loading,
  onCancel,
  onSubmit,
  override = { countryCode: '', ipAddress: '', oktaId: '', zipCode: '' },
}: OverrideFormProps) {
  const form = useForm<OverrideFormValues>({
    defaultValues: override,
    disabled: loading,
    resolver: zodResolver(schema),
  });

  const handleReset = () => {
    if (loading) return;
    form.reset();
    onCancel();
  };

  return (
    <Form {...form}>
      <form className="space-y-4 pt-2" onReset={handleReset} onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="countryCode"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Country Code</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name="oktaId"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Okta ID</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="ipAddress"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>IP Address</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="zipCode"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Zip Code</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <DialogFooter>
          <Button disabled={loading} type="reset" variant="secondary">
            Cancel
          </Button>
          <Button aria-label="Save Override" disabled={loading} loading={loading} type="submit">
            Save Override
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
